import { useState, useEffect, useRef } from 'react';
import { adminService } from '@/api/services/admin';
import type { User } from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// ── Grant credits section ─────────────────────────────────────────────────────

function GrantCreditsSection({ users }: { users: User[] }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<User | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [numCredits, setNumCredits] = useState(15);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = q
    ? users.filter(
        (u) =>
          u.full_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q) ||
          u.org_name.toLowerCase().includes(q),
      ).slice(0, 8)
    : [];

  const handleSelect = (u: User) => {
    setSelected(u);
    setQuery('');
    setShowDropdown(false);
    setSuccess('');
    setError('');
  };

  const handleClearSelection = () => {
    setSelected(null);
    setSuccess('');
    setError('');
  };

  const handleSubmit = async () => {
    if (!selected) {
      setError('Select a user first.');
      return;
    }
    if (!numCredits || numCredits < 1) {
      setError('Credits must be at least 1.');
      return;
    }
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      const result = await adminService.addCredits(selected.id, numCredits);
      const newBalance = result?.available_credits;
      setSuccess(
        newBalance !== undefined
          ? `Granted ${numCredits} credits to ${selected.email}. New balance: ${newBalance.toLocaleString()}.`
          : `Granted ${numCredits} credits to ${selected.email}.`,
      );
      setNumCredits(15);
      setSelected(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to add credits.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Grant credits</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-w-md">
          {success && (
            <div className="p-3 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/30 rounded-md">
              <p className="text-sm text-success-700 dark:text-success-400">{success}</p>
            </div>
          )}
          {error && (
            <div className="p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/30 rounded-md">
              <p className="text-sm text-error-700 dark:text-error-400">{error}</p>
            </div>
          )}

          {/* Selected user badge */}
          {selected && (
            <div className="flex items-center gap-2">
              <Badge variant="primary" size="sm">{selected.email}</Badge>
              <button
                onClick={handleClearSelection}
                className="text-xs text-text-muted hover:text-text-primary transition-colors"
                aria-label="Clear selection"
              >
                ✕
              </button>
            </div>
          )}

          {/* User picker */}
          {!selected && (
            <div ref={containerRef} className="relative">
              <Input
                label="Search user"
                placeholder="Name, email, or org…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setShowDropdown(true);
                }}
                onFocus={() => setShowDropdown(true)}
              />
              {showDropdown && matches.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-surface-elevated border border-border rounded-lg shadow-lg max-h-52 overflow-y-auto">
                  {matches.map((u) => (
                    <button
                      key={u.id}
                      className="w-full text-left px-3 py-2 hover:bg-surface transition-colors"
                      onMouseDown={(e) => { e.preventDefault(); handleSelect(u); }}
                    >
                      <p className="text-sm font-medium text-text-primary">{u.full_name || u.email}</p>
                      <p className="text-xs text-text-secondary">{u.email} {u.org_name ? `· ${u.org_name}` : ''}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Credits input */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">Credits to grant</label>
            <input
              type="number"
              min={1}
              value={numCredits}
              onChange={(e) => {
                const n = parseInt(e.target.value, 10);
                setNumCredits(Number.isFinite(n) ? n : 1);
              }}
              className="w-28 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:opacity-50"
              disabled={submitting}
            />
          </div>

          <Button
            variant="primary"
            disabled={!selected || submitting}
            onClick={handleSubmit}
          >
            {submitting ? 'Granting…' : 'Grant credits'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Credits page ──────────────────────────────────────────────────────────────

export default function Credits() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsers = () => {
    setLoading(true);
    setError('');
    adminService
      .getUsers()
      .then((data) => setUsers(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-text-primary">Credits</h1>
        <div className="flex items-center gap-3 p-3 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-700 dark:text-error-400 flex-1">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchUsers}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-text-primary">Credits</h1>

      <GrantCreditsSection users={users} />

      {/* Stub: Usage by action */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Usage by action</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted italic">
            Usage-by-action endpoint pending — needs{' '}
            <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">GET /adm/credits_usage?bucket=day&amp;since=X</code>.
          </p>
        </CardContent>
      </Card>

      {/* Stub: Ledger */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Ledger</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-elevated">
                  {['Timestamp', 'User', 'Action', 'Delta', 'Balance after', 'Reason'].map((col) => (
                    <th key={col} className="px-4 py-2.5 text-left text-xs font-medium text-text-muted uppercase tracking-wide whitespace-nowrap">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-sm text-text-muted italic">
                    Needs{' '}
                    <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">
                      GET /adm/credits_ledger?since=X&amp;user=&amp;action=
                    </code>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Stub: Stripe sync */}
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>Stripe sync</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted italic">
            Stripe reconciliation status pending — needs backend webhook health endpoint.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
